/**
 * URL 기반 라우트 설정
 * 상태 기반 라우팅에서 URL 기반 라우팅으로 전환
 */

// 라우트 경로 상수
export const ROUTES = {
  // 메인 탭 라우트
  HOME: '/',
  ORDERS: '/orders',
  QR: '/qr',
  QR_VERIFY: '/qr/:qrCode',
  POINTS: '/points',
  ADMIN: '/admin',

  // 레스토랑/메뉴 라우트
  RESTAURANT: '/restaurant/:id',
  RESTAURANT_TABLE: '/restaurant/:id/table/:tableNum',
  RESTAURANT_TABLE_CHECKOUT: '/restaurant/:id/table/:tableNum/checkout',

  // 조직/브랜드/매장 계층 라우트
  ORGANIZATIONS: '/organizations',
  ORGANIZATION_DETAIL: '/org/:orgParam',
  ORGANIZATION_BRANDS: '/org/:orgParam/brands',
  BRAND_STORES: '/org/:orgParam/brand/:brandParam',

  // 장바구니/결제/영수증 라우트
  CART: '/cart',
  CHECKOUT: '/checkout',
  PAYMENT: '/payment',
  PAYMENT_RESULT: '/payment/result',
  RECEIPT: '/receipt/:orderId',
  FACTURACION: '/facturacion',
  COMPANY_LIST: '/company-list',

  // 인증 라우트
  LOGIN: '/login',
  VERIFY_EMAIL: '/verify/email',
  VERIFY_BIRTHDAY: '/verify/birthday',

  // 프로필/설정 하위 라우트
  PROFILE: '/profile',
  ORDER_HISTORY: '/order-history',
  PAYMENT_METHOD: '/payment-method',
  PRIVACY_SETTINGS: '/privacy-settings',

  // 정보 페이지 라우트
  FAQ: '/faq',
  FAQ_DETAIL: '/faq/:id',
  ANNOUNCEMENTS: '/announcements',
  ANNOUNCEMENT_DETAIL: '/announcements/:id',
  LEGAL: '/legal',
  TERMS: '/terms',
  PRIVACY: '/privacy',

  // 세션 라우트
  SESSION_EXPIRED: '/session-expired',
  // #661: 테이블 세션 전체 결제 전용 페이지 (모달 → 페이지 전환)
  TABLE_SESSION_CHARGE: '/table-session/charge',

  // 줄서기(Fila Virtual) — QR 진입 전용 고객 화면 (#781 / admin#2111)
  WAITLIST: '/fila/:storeSlug',

  // 포인트 하위 라우트
  POINTS_FRANCHISE: '/points/franchise/:franchiseId',
  POINTS_STORE: '/points/franchise/:franchiseId/store/:storeId',
} as const;

// 설정(admin) 하위 페이지 목록
export const ADMIN_SUB_ROUTES = [
  ROUTES.PROFILE,
  ROUTES.ORDER_HISTORY,
  ROUTES.PAYMENT_METHOD,
  ROUTES.PRIVACY_SETTINGS,
  ROUTES.ANNOUNCEMENTS,
  ROUTES.LEGAL,
  ROUTES.TERMS,
  ROUTES.PRIVACY,
] as const;

// 탭 ID와 라우트 매핑
export const TAB_ROUTES: Record<string, string> = {
  home: ROUTES.HOME,
  orders: ROUTES.ORDERS,
  qr: ROUTES.QR,
  points: ROUTES.POINTS,
  admin: ROUTES.ADMIN,
};

// 라우트에서 탭 ID 추출
export function getTabFromPath(pathname: string): string {
  if (isCheckoutPath(pathname)) {
    return 'home'; // 체크아웃 페이지는 home 탭으로 간주
  }

  switch (pathname) {
    case '/':
      return 'home';
    case '/orders':
      return 'orders';
    case '/qr':
      return 'qr';
    case '/points':
      return 'points';
    case '/admin':
      return 'admin';
    case '/payment':
      return 'home'; // 결제 페이지는 home 탭으로 간주
    default:
      // /qr/{code} 경로는 qr 탭
      if (pathname.startsWith('/qr/')) {
        return 'qr';
      }
      // 포인트 하위 페이지는 points 탭으로 간주
      if (pathname.startsWith('/points')) {
        return 'points';
      }
      // 레스토랑 메뉴 페이지는 home 탭으로 간주
      if (pathname.startsWith('/restaurant')) {
        return 'home';
      }
      // 설정 하위 페이지들은 admin 탭으로 간주
      if (isAdminSubRoute(pathname)) {
        return 'admin';
      }
      // 인증 페이지들은 탭 없음 (home 반환)
      if (pathname.startsWith('/login') || pathname.startsWith('/verify')) {
        return 'home';
      }
      // 영수증 페이지는 home 탭으로 간주
      if (pathname.startsWith('/receipt')) {
        return 'home';
      }
      // 조직/브랜드 페이지는 home 탭으로 간주
      if (pathname === '/organizations' || pathname.startsWith('/org/')) {
        return 'home';
      }
      return 'home';
  }
}

// 설정 하위 페이지인지 확인
export function isAdminSubRoute(pathname: string): boolean {
  // 정적 라우트 체크
  if (ADMIN_SUB_ROUTES.some(route => pathname === route)) {
    return true;
  }
  // 동적 라우트 체크 (공지사항 상세)
  if (pathname.startsWith('/announcements/')) {
    return true;
  }
  return false;
}

// 레스토랑 라우트 헬퍼
export function getRestaurantPath(id: string): string {
  return `/restaurant/${id}`;
}

export function getRestaurantTablePath(id: string, tableNum: string): string {
  return `/restaurant/${id}/table/${tableNum}`;
}

export function getRestaurantTableCheckoutPath(id: string, tableNum: string): string {
  return `${getRestaurantTablePath(id, tableNum)}/checkout`;
}

export function isCheckoutPath(pathname: string): boolean {
  return /^\/checkout\/?$/.test(pathname) ||
    /^\/restaurant\/[^/]+\/table\/[^/]+\/checkout\/?$/.test(pathname);
}

// 영수증 라우트 헬퍼
export function getReceiptPath(orderId: string): string {
  return `/receipt/${orderId}`;
}

// 세금계산서 라우트 헬퍼 (#877)
export function getFacturacionPath(params?: {
  order_id?: string | number;
  folio?: string;
  date?: string;
  total?: string | number;
  store_id?: string | number;
}): string {
  if (!params) return ROUTES.FACTURACION;
  const searchParams = new URLSearchParams();
  if (params.order_id) searchParams.set('order_id', String(params.order_id));
  if (params.folio) searchParams.set('folio', params.folio);
  if (params.date) searchParams.set('date', params.date);
  if (params.total) searchParams.set('total', String(params.total));
  if (params.store_id) searchParams.set('store_id', String(params.store_id));
  const qs = searchParams.toString();
  return qs ? `${ROUTES.FACTURACION}?${qs}` : ROUTES.FACTURACION;
}

// FAQ 상세 라우트 헬퍼
export function getFaqDetailPath(faqId: string): string {
  return `/faq/${faqId}`;
}

// 공지사항 상세 라우트 헬퍼
export function getAnnouncementDetailPath(announcementId: string): string {
  return `/announcements/${announcementId}`;
}

// 포인트 프랜차이즈 라우트 헬퍼
export function getPointsFranchisePath(franchiseId: string): string {
  return `/points/franchise/${franchiseId}`;
}

// 포인트 매장 상세 라우트 헬퍼
export function getPointsStorePath(franchiseId: string, storeId: string): string {
  return `/points/franchise/${franchiseId}/store/${storeId}`;
}
